import { lazy, Suspense, useCallback, useState } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DepotDataProvider } from './context/DepotDataContext';
import { AppShell } from './components/layout/AppShell';
import { CommandPalette } from './components/layout/CommandPalette';
import { LoginPage } from './features/auth/LoginPage';
import { useHashRoute } from './hooks/useHashRoute';
import { useHotkey } from './hooks/useHotkey';
import { ROUTES } from './constants/navigation';
import { Spinner, Toaster } from './components/ui';

// Widoki ładowane leniwie.
//
// To jest główna zmiana wydajnościowa całego refactoru. Wcześniej App importował
// Dashboard, ten importował ContainerManagement i StockManagement, a te z kolei
// jsPDF, osadzony w kodzie font (774 kB base64) i ExcelJS (925 kB). Wszystko
// trafiało do jednego pliku JS pobieranego jeszcze przed ekranem logowania —
// około 2,5 MB, z czego zdecydowana większość obsługiwała funkcje uruchamiane
// sporadycznie. Teraz każdy widok to osobna paczka pobierana przy pierwszym
// wejściu, a same biblioteki raportowe dopiero przy kliknięciu "Generuj".
const DashboardPage = lazy(() => import('./features/dashboard/DashboardPage'));
const ContainersPage = lazy(() => import('./features/containers/ContainersPage'));
const StocksPage = lazy(() => import('./features/stocks/StocksPage'));
const ReportsPage = lazy(() => import('./features/reports/ReportsPage'));

function FullScreenLoader({ label = 'Ładowanie…' }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-app">
      <div className="flex items-center gap-3 text-sm text-muted">
        <Spinner />
        {label}
      </div>
    </div>
  );
}

function ViewLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner />
    </div>
  );
}

function Workspace() {
  const [route, navigate] = useHashRoute(ROUTES.DASHBOARD);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Kontener wskazany z wyszukiwarki: widok kontenerów otwiera dla niego
  // szczegóły, a następnie zgłasza obsłużenie, żeby modal nie otwierał się
  // ponownie przy każdym kolejnym renderze.
  const [focusContainerId, setFocusContainerId] = useState(null);

  useHotkey('mod+k', () => setIsSearchOpen(true));

  const openContainer = useCallback(
    (container) => {
      setFocusContainerId(container.id);
      navigate(ROUTES.CONTAINERS);
    },
    [navigate]
  );

  const renderView = () => {
    switch (route) {
      case ROUTES.CONTAINERS:
        return (
          <ContainersPage
            focusContainerId={focusContainerId}
            onFocusHandled={() => setFocusContainerId(null)}
          />
        );
      case ROUTES.STOCKS:
        return <StocksPage onNavigate={navigate} />;
      case ROUTES.REPORTS:
        return <ReportsPage onNavigate={navigate} />;
      case ROUTES.DASHBOARD:
      default:
        return <DashboardPage onNavigate={navigate} onOpenContainer={openContainer} />;
    }
  };

  return (
    <>
      <AppShell route={route} onNavigate={navigate} onOpenSearch={() => setIsSearchOpen(true)}>
        <Suspense fallback={<ViewLoader />}>{renderView()}</Suspense>
      </AppShell>

      <CommandPalette
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onNavigate={navigate}
        onOpenContainer={openContainer}
      />
    </>
  );
}

function AuthenticatedApp() {
  const { isAuthReady, user } = useAuth();

  if (!isAuthReady) return <FullScreenLoader label="Sprawdzanie sesji…" />;
  if (!user) return <LoginPage />;

  return (
    <DepotDataProvider>
      <Workspace />
    </DepotDataProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <AuthenticatedApp />
          <Toaster />
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
